// Private affiliate portal (public route). Handles its own auth: Continue with
// Google, then enter the shared access password once (→ becomes an affiliate).
// No app login wall, no clipping onboarding — affiliates are a separate user
// type. Their outreach board persists server-side so the admin can see it.
import { useEffect, useState, useCallback, useContext } from "react";
import { axiosInstance } from "../../config";
import { AuthContext } from "../../context/AuthContext";
import "./affiliatePortal.css";

const SCRIPTS = [
  {
    label: "Opener (personalize the bracket first)",
    text: `Hey [name], saw [specific detail: your agency runs a big Content Rewards campaign / you're managing a ton of creator submissions on Whop / etc.]. Most agencies I talk to have gotten burned at some point by a "mod" who needed access to the campaign to review clips, and that access is exactly how budgets end up misused or submissions get missed.

I work with a Chrome extension that kills manual reviewing. It auto-reviews Content Rewards clip submissions against your own campaign brief, approving or rejecting with written reasons, so you can stop relying on mods and work faster and more efficiently.

Worth a 2 minute look? Here's a demo: https://youtu.be/oPIEdIOhleA?si=GXN9RzCKwP9Waq6s. If that's not useful right now, all good, no need to reply.`,
  },
  {
    label: "If they reply with interest / watch the demo → SEND YOUR LINK HERE",
    text: `Glad it was useful.

Worth mentioning too, the founder & CEO of Content Rewards has spoken positively about our clip review bot: https://x.com/spiritraps/status/2084726104879714392

$299/mo, 3 team seats included, works across IG/TikTok/YouTube clips. First month's 50% off with code LAUNCH50 ($149.50).

Here's where to grab it: https://whop.com/clipbait-ai/clipbait-review-ai?a=YOUR_USERNAME
(^ swap YOUR_USERNAME for your Whop affiliate username — this is what credits the sale to you.)`,
  },
  {
    label: "Follow-up (no reply after 3 to 4 days)",
    text: `No pressure if now's not the right time, but I wanted to reach out because we solve the headache of manual clip reviewing and the annoyance of mods constantly pitching you. This ends the need to hire mods to review clips and saves you money, so you can keep more for your clipping budget.

Worth noting too, the founder & CEO of Content Rewards has spoken positively about our clip review bot: https://x.com/spiritraps/status/2084726104879714392

Demo's still here if useful later: https://youtu.be/oPIEdIOhleA?si=GXN9RzCKwP9Waq6s. Either way, good luck with the campaigns. If that's not useful right now, all good, no need to reply.`,
  },
];

const OBJECTIONS = [
  {
    label: '"We already have a mod/reviewer."',
    text: `Makes sense, but with this tool, you wouldn't need to hire more when scaling. Your existing mod can process clips much faster, freeing them up to engage with clippers and focus on tasks beyond mundane clip reviewing that doesn't really add much value to your agency.`,
  },
  {
    label: '"Is this another fake tool / another mod scam?"',
    text: `Totally fair to be skeptical. This is an official Chrome extension verified by the Google Web Store. Plus, the founder & CEO of Content Rewards has spoken positively about it: https://x.com/spiritraps/status/2084726104879714392`,
  },
  {
    label: '"How much?"',
    text: `$299/mo, 3 seats included. We're currently offering 50% off the first month for the first 50 agencies with code LAUNCH50. There's no real downside to testing it.`,
  },
];

const COMMISSION = 74.75; // $/mo recurring per referral

// Rotating motivational line (changes daily) — keep the affiliate fired up.
const MOTIVATION = [
  "One good DM a day compounds into rent-money recurring. Show up.",
  "You're not asking for a favor — you're saving agencies from mod scams.",
  "Volume beats perfection. 50 decent DMs > 5 perfect ones.",
  "Every reply is a maybe. Every follow-up turns maybes into money.",
  "The founder of Content Rewards vouched for this. You're selling a winner.",
  "10 signups = $747.50/mo — forever, while they stay. That's the game.",
  "Consistency is the whole cheat code. Same time, every day.",
];
function todaysMotivation() {
  return MOTIVATION[Math.floor(Date.now() / 86400000) % MOTIVATION.length];
}

const TIPS = [
  { t: "Personalize the first line", d: "Name their actual campaign or a recent submission. Generic openers get ignored; one specific detail earns the read." },
  { t: "Lead with the demo, not the pitch", d: "Cold prospects need proof before price. Send the 2-min demo first, hold your affiliate link until they're interested." },
  { t: "Always follow up once", d: "Most replies come on the follow-up, not the opener. The board flags who's due — clear that list daily." },
  { t: "Target agencies running Content Rewards", d: "Whop + Discord clipping campaigns are the sweet spot — they feel the mod-review pain the hardest." },
  { t: "Send your affiliate link the moment they're in", d: "That ?a= link is the only thing that credits the sale to you. No link, no commission." },
  { t: "Batch it", d: "Block 30–45 min, fire ~50 DMs, log them here. Momentum + tracking beats sporadic effort every time." },
];

function daysSince(dateStr) {
  const then = new Date(dateStr);
  return Math.floor((Date.now() - then.getTime()) / 86400000);
}
function getStage(c) {
  if (c.signedUp) return { label: "Signed up", cls: "b-done" };
  if (c.demoViewed) return { label: "Demo viewed", cls: "b-active" };
  if (c.replied) return { label: "Replied, send demo", cls: "b-active" };
  if (c.followupSent) return { label: "Follow-up sent", cls: "b-waiting" };
  if (daysSince(c.dateSent) >= 3) return { label: "Follow-up due", cls: "b-due" };
  return { label: "Awaiting reply", cls: "b-waiting" };
}

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className={`ap-copy${copied ? " copied" : ""}`}
      onClick={() => navigator.clipboard?.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })}
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function Panel({ title, children, open }) {
  return (
    <details className="ap-panel" open={open}>
      <summary>{title}</summary>
      <div className="ap-panel-body">{children}</div>
    </details>
  );
}

export default function AffiliatePortal() {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [affiliate, setAffiliate] = useState(null);
  const [accessConfigured, setAccessConfigured] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [nameInput, setNameInput] = useState("");
  // Gate
  const [pw, setPw] = useState("");
  const [joining, setJoining] = useState(false);
  const [gateErr, setGateErr] = useState("");
  const [showBonus, setShowBonus] = useState(false);

  useEffect(() => {
    if (!user) { setLoading(false); return; } // signed-out screen handles it
    setLoading(true);
    axiosInstance
      .get("/affiliates/me")
      .then((r) => {
        setAffiliate(r.data.affiliate);
        setAccessConfigured(r.data.accessConfigured !== false);
        if (r.data.affiliate) setContacts(r.data.affiliate.contacts || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const signInWithGoogle = () => {
    try { sessionStorage.setItem("cb_post_auth_return", "/affiliates"); } catch { /* ignore */ }
    window.location.href = axiosInstance.defaults.baseURL + "/auth/google";
  };

  // Persist the board (fire-and-forget; the server sanitizes).
  const persist = useCallback((next) => {
    axiosInstance.put("/affiliates/me/contacts", { contacts: next }).catch(() => {});
  }, []);

  const commit = (next) => { setContacts(next); persist(next); };

  const join = async () => {
    setJoining(true);
    setGateErr("");
    try {
      const r = await axiosInstance.post("/affiliates/join", { password: pw });
      setAffiliate(r.data.affiliate);
      setContacts(r.data.affiliate?.contacts || []);
    } catch (e) {
      setGateErr(e.response?.data?.message || "Couldn't verify access.");
    } finally {
      setJoining(false);
    }
  };

  const addContact = () => {
    const name = nameInput.trim();
    if (!name) return;
    const wasFirst = contacts.length === 0;
    commit([
      { cid: Date.now(), name, platform: "Whop", dateSent: new Date().toISOString().slice(0, 10), replied: false, demoViewed: false, followupSent: false, signedUp: false },
      ...contacts,
    ]);
    setNameInput("");
    // First agency logged → show the $100-for-100-DMs bonus once.
    if (wasFirst && !localStorage.getItem("ap_bonus_seen")) {
      try { localStorage.setItem("ap_bonus_seen", "1"); } catch { /* ignore */ }
      setShowBonus(true);
    }
  };
  const toggle = (cid, field) => commit(contacts.map((c) => (c.cid === cid ? { ...c, [field]: !c[field] } : c)));
  const remove = (cid) => commit(contacts.filter((c) => c.cid !== cid));

  if (loading) return <div className="ap-page"><div className="ap-wrap"><div className="ap-empty">Loading…</div></div></div>;

  // ── Signed out: portal-branded Google sign-in (no app login wall) ─────────
  if (!user) {
    return (
      <div className="ap-page">
        <div className="ap-wrap ap-gate">
          <h1>Clipbait affiliate portal</h1>
          <p className="ap-sub">Earn $74.75/mo recurring per referral. Sign in with Google, then enter your access code.</p>
          <button className="ap-btn ap-google" onClick={signInWithGoogle}>Continue with Google</button>
        </div>
      </div>
    );
  }

  // ── Gate: signed in but not an affiliate yet ──────────────────────────────
  if (!affiliate) {
    return (
      <div className="ap-page">
        <div className="ap-wrap ap-gate">
          <h1>Affiliate portal</h1>
          <p className="ap-sub">Enter the access password to join the affiliate program.</p>
          {!accessConfigured ? (
            <div className="ap-gate-closed">The affiliate program isn't open yet. Check back soon.</div>
          ) : (
            <>
              <input
                type="password"
                className="ap-input"
                placeholder="Access password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && join()}
              />
              {gateErr && <div className="ap-err">{gateErr}</div>}
              <button className="ap-btn" onClick={join} disabled={joining || !pw}>
                {joining ? "Checking…" : "Enter portal"}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Board ─────────────────────────────────────────────────────────────────
  const sent = contacts.length;
  const replied = contacts.filter((c) => c.replied).length;
  const signed = contacts.filter((c) => c.signedUp).length;
  const rate = sent ? Math.round((replied / sent) * 100) : 0;
  const today = new Date().toISOString().slice(0, 10);
  const todayDMs = contacts.filter((c) => c.dateSent === today).length;
  const BONUS_TARGET = 100;
  const DAILY_TARGET = 50;
  const earning = signed * COMMISSION;
  const driver =
    sent === 0
      ? "Every $74.75/mo starts with one DM. Log your first agency and go."
      : signed === 0
        ? `${sent} DM${sent === 1 ? "" : "s"} logged. Replies become signups — stay consistent and follow up.`
        : `${signed} signup${signed === 1 ? "" : "s"} = $${earning.toFixed(2)}/mo recurring. Next stop: 10 = $747.50/mo.`;

  return (
    <div className="ap-page">
      <div className="ap-wrap">
        <h1>Outreach tracker</h1>
        <p className="ap-sub">
          Log who you've DMed and what's happened. Everything else updates on its own.
        </p>

        <div className="ap-driver">
          <div className="ap-driver-earn">${earning.toFixed(2)}<span>/mo</span></div>
          <div className="ap-driver-body">
            <div className="ap-driver-label">Your recurring earnings</div>
            <div className="ap-driver-msg">{driver}</div>
          </div>
        </div>
        <div className="ap-motivation">“{todaysMotivation()}”</div>

        <Panel title="Scripts to send — copy, personalize, send" open>
          {SCRIPTS.map((s, i) => (
            <div className="ap-snippet" key={i}>
              <div className="ap-snippet-lbl">{s.label}</div>
              <div className="ap-snippet-txt">{s.text}</div>
              <CopyBtn text={s.text} />
            </div>
          ))}
        </Panel>

        <Panel title="How to earn — get your affiliate link" open>
          <div className="ap-ref"><span className="l">Commission</span><span className="v">$74.75/month per referral</span></div>
          <p className="ap-para">
            <b>1.</b> Sign up as an affiliate on Whop:{" "}
            <a href="https://whop.com/clipbait-ai/affiliates" target="_blank" rel="noreferrer">whop.com/clipbait-ai/affiliates</a>
          </p>
          <p className="ap-para"><b>2.</b> Once you're in, you get your <b>unique affiliate link</b>. Share it with clipping agencies.</p>
          <p className="ap-para">
            <b>3.</b> Anyone who clicks your link and buys = you earn <b>$74.75/month</b> for as long as they stay subscribed.
          </p>
          <div className="ap-snippet">
            <div className="ap-snippet-lbl">Your affiliate link looks like</div>
            <div className="ap-snippet-txt ap-mono">https://whop.com/clipbait-ai/clipbait-review-ai?a=YOUR_USERNAME</div>
          </div>
          <p className="ap-para ap-note">
            <b>When to send your link:</b> open with the <b>demo video</b>, not your link — cold
            prospects need proof first. The moment they show interest or ask "how do I get it?",
            send <b>your affiliate link</b> (it's the checkout page). That link is the only thing
            that credits the sale to you — if they buy any other way, you don't get paid.
          </p>
        </Panel>

        <Panel title="The playbook — approach & tracking">
          <p className="ap-para">
            <b>The approach.</b> These are cold DMs to people who've never heard of you.
            Lead with the risk agencies already carry from mod-based review, then let the
            demo video do the proving. Ask for nothing upfront beyond 2 minutes of their attention.
          </p>
          <div className="ap-ref"><span className="l">Your cut</span><span className="v">$74.75/mo recurring per referral (of $299/mo)</span></div>
          <div className="ap-ref"><span className="l">As long as they stay</span><span className="v">You keep earning every month they're subscribed</span></div>
          <div className="ap-ref"><span className="l">Do the math</span><span className="v">10 referrals = $747.50/mo passive</span></div>
          <p className="ap-para">
            <b>Tracking.</b> Log every DM below as you send it. Mark replies, demo views, and
            sign-ups — the board shows your reply rate and flags when a follow-up is due, no
            manual math. Your admin sees this too.
          </p>
        </Panel>

        <Panel title="Quick reference">
          <div className="ap-ref"><span className="l">Product</span><span className="v">Content Rewards Clip Reviewer (Chrome extension)</span></div>
          <div className="ap-ref"><span className="l">Price</span><span className="v">$299/mo · 3 seats · IG / TikTok / YouTube</span></div>
          <div className="ap-ref"><span className="l">Launch code</span><span className="v">LAUNCH50 — 50% off first month ($149.50), first 50 agencies</span></div>
          <div className="ap-ref"><span className="l">Social proof</span><span className="v"><a href="https://x.com/spiritraps/status/2084726104879714392" target="_blank" rel="noreferrer">Daniel Bitton (CEO, Content Rewards) tweet</a></span></div>
          <div className="ap-ref"><span className="l">Demo video</span><span className="v"><a href="https://youtu.be/oPIEdIOhleA?si=GXN9RzCKwP9Waq6s" target="_blank" rel="noreferrer">Watch</a></span></div>
        </Panel>

        <Panel title="Objection handling">
          {OBJECTIONS.map((o, i) => (
            <div className="ap-snippet" key={i}>
              <div className="ap-snippet-lbl">{o.label}</div>
              <div className="ap-snippet-txt">{o.text}</div>
              <CopyBtn text={o.text} />
            </div>
          ))}
        </Panel>

        <Panel title="Tips to close more">
          {TIPS.map((tip, i) => (
            <div className="ap-tip" key={i}>
              <div className="ap-tip-t">{tip.t}</div>
              <div className="ap-tip-d">{tip.d}</div>
            </div>
          ))}
        </Panel>

        <div className="ap-stats">
          <div className="ap-stat"><div className="num">{sent}</div><div className="lbl">DMs sent</div></div>
          <div className="ap-stat"><div className="num">{replied}</div><div className="lbl">Replies</div></div>
          <div className="ap-stat"><div className="num">{rate}%</div><div className="lbl">Reply rate</div></div>
          <div className="ap-stat"><div className="num">{signed}</div><div className="lbl">Signed up</div></div>
        </div>

        <div className="ap-goals">
          <div className="ap-goal">
            <div className="ap-goal-head">
              <span>$100 bonus — DM {BONUS_TARGET} agencies</span>
              <span className="ap-goal-num">{Math.min(sent, BONUS_TARGET)}/{BONUS_TARGET}</span>
            </div>
            <div className="ap-goal-bar"><div className="ap-goal-fill" style={{ width: `${Math.min(100, (sent / BONUS_TARGET) * 100)}%` }} /></div>
            {sent >= BONUS_TARGET && <div className="ap-goal-done">🎉 Bonus unlocked — your $100 is on the way.</div>}
          </div>
          <div className="ap-goal">
            <div className="ap-goal-head">
              <span>Suggested pace — ~{DAILY_TARGET} DMs a day</span>
              <span className="ap-goal-num">{todayDMs} today</span>
            </div>
            <div className="ap-goal-bar"><div className="ap-goal-fill" style={{ width: `${Math.min(100, (todayDMs / DAILY_TARGET) * 100)}%` }} /></div>
            {todayDMs >= DAILY_TARGET
              ? <div className="ap-goal-done">🔥 {todayDMs} today — that's the pace. Every extra DM gets you to the bonus faster.</div>
              : <div className="ap-goal-hint">No pressure — ~{DAILY_TARGET}/day is the pace that lands the $100 bonus in about two days.</div>}
          </div>
        </div>

        <div className="ap-add">
          <input
            type="text"
            className="ap-input"
            placeholder="Agency or contact name"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addContact()}
          />
          <button className="ap-btn" onClick={addContact}>Add</button>
        </div>

        {contacts.length === 0 ? (
          <div className="ap-empty">No contacts yet. Add the first agency you DM above.</div>
        ) : (
          contacts.map((c) => {
            const stage = getStage(c);
            return (
              <div className="ap-card" key={c.cid}>
                <div className="ap-card-top">
                  <div>
                    <div className="ap-name">{c.name}</div>
                    <div className="ap-meta">{c.platform} · sent {c.dateSent}</div>
                  </div>
                  <div className={`ap-badge ${stage.cls}`}>{stage.label}</div>
                </div>
                <div className="ap-actions">
                  <button className={c.replied ? "on" : ""} onClick={() => toggle(c.cid, "replied")}>Replied</button>
                  <button className={c.demoViewed ? "on" : ""} onClick={() => toggle(c.cid, "demoViewed")}>Demo viewed</button>
                  <button className={c.followupSent ? "on" : ""} onClick={() => toggle(c.cid, "followupSent")}>Follow-up sent</button>
                  <button className={c.signedUp ? "on" : ""} onClick={() => toggle(c.cid, "signedUp")}>Signed up</button>
                  <button className="ap-del" onClick={() => remove(c.cid)}>Remove</button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {showBonus && (
        <div className="ap-modal-overlay" onClick={() => setShowBonus(false)}>
          <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ap-modal-emoji">💸</div>
            <h2 className="ap-modal-title">$100 bonus</h2>
            <p className="ap-modal-sub">
              First one logged — nice. DM <b>100 agencies</b> and we'll send you <b>$100</b> on top
              of your commissions. A pace of ~50 a day gets you there in about two days.
            </p>
            <button className="ap-btn" onClick={() => setShowBonus(false)}>Let's go</button>
          </div>
        </div>
      )}
    </div>
  );
}
